import type { AnalyticsEvent } from "@/types/models";

const GOOGLE_ANALYTICS_MEASUREMENT_ID = "G-DZGCE7C5TF";
const ANALYTICS_EVENT_STORAGE_PREFIX = "awraq:analytics-event:";
const sentEvents = new Set<string>();

export type GoogleAnalyticsParameters = Record<string, string | number | boolean | undefined>;

type GoogleTagArguments =
  | [command: "js", date: Date]
  | [command: "config", measurementId: string, parameters: GoogleAnalyticsParameters]
  | [command: "event", eventName: string, parameters: GoogleAnalyticsParameters];

type GoogleTag = (...args: GoogleTagArguments) => void;

type GoogleAnalyticsWindow = Window & {
  awraqGoogleAnalyticsInitialized?: boolean;
  dataLayer?: IArguments[];
  gtag?: GoogleTag;
};

/**
 * The sole client-side Google Analytics boundary.
 * app.tsx owns the Inertia page lifecycle; feature modules send named events through this file.
 */
let enabled = false;

/** Turns Google Analytics on for a real production visit; until then every call here does nothing. */
export function enableGoogleAnalytics(): void {
  enabled = true;
  initializeGoogleAnalytics();
}

function initializeGoogleAnalytics(): void {
  const analyticsWindow = window as GoogleAnalyticsWindow;

  if (!enabled || analyticsWindow.awraqGoogleAnalyticsInitialized) {
    return;
  }

  analyticsWindow.awraqGoogleAnalyticsInitialized = true;
  const dataLayer = (analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? []);
  analyticsWindow.gtag = function googleTag(): void {
    dataLayer.push(arguments);
  };

  const scriptUrl = new URL("https://www.googletagmanager.com/gtag/js");
  scriptUrl.searchParams.set("id", GOOGLE_ANALYTICS_MEASUREMENT_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = scriptUrl.toString();
  document.head.append(script);

  analyticsWindow.gtag("js", new Date());
  // Inertia's navigate event owns initial, subsequent, and history page views.
  analyticsWindow.gtag("config", GOOGLE_ANALYTICS_MEASUREMENT_ID, { send_page_view: false });
}

export function trackGoogleAnalyticsEvent(eventName: string, parameters: GoogleAnalyticsParameters): void {
  if (!enabled) {
    return;
  }

  initializeGoogleAnalytics();

  const analyticsWindow = window as GoogleAnalyticsWindow;
  analyticsWindow.gtag?.("event", eventName, parameters);
}

export function trackGoogleAnalyticsPageView(pageUrl: string): void {
  trackGoogleAnalyticsEvent("page_view", {
    page_location: new URL(pageUrl, window.location.origin).href,
  });
}

export function trackGoogleAnalyticsEventOnce(event?: AnalyticsEvent | null): void {
  if (!event || sentEvents.has(event.id)) {
    return;
  }

  // Flashed Inertia props can survive re-renders, so deduplicate by the server-generated event ID.
  const storageKey = `${ANALYTICS_EVENT_STORAGE_PREFIX}${event.id}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }
  } catch {
    // Storage may be disabled; the in-memory guard still deduplicates this visit.
  }

  trackGoogleAnalyticsEvent(event.name, {
    ...event.parameters,
    event_id: event.id,
  });
  sentEvents.add(event.id);

  try {
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // persistence-best-effort: the in-memory guard deduplicates; storage failure must not interrupt onboarding.
  }
}
